import { useEffect } from "react";
import { getMetaData } from "../../utils/Constant";

const ThreeGPFormatGuide = () => {

  // Set title and metadata
  useEffect(() => {
    const metaData = getMetaData("3gp-meta-data");

    document.title = metaData.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = metaData.description;
  }, []);

  return (
    <div className="container mt-5 mb-5">
      <article className="bg-white rounded-4 shadow-lg border-0 overflow-hidden">
        {/* Header Section */}
        <header
          className="text-center py-5 px-3 position-relative"
          style={{
            background: "linear-gradient(135deg, #6c757d 0%, #495057 100%)",
            color: "#fff",
          }}
        >
          <h1 className="display-5 fw-bold mb-3 position-relative">
           3GP File Format: Legacy, Uses, and Conversions
          </h1>
          <p className="lead mb-0 position-relative fs-4">
            Understanding the format that brought video to early mobile phones
          </p>
          <div className="mt-4 position-relative">
            <span className="badge bg-light text-secondary fs-6 me-2 p-2">
              Mobile Video
            </span>
            <span className="badge bg-light text-secondary fs-6 me-2 p-2">
              Legacy Format
            </span>
            <span className="badge bg-light text-secondary fs-6 p-2">
              Mobile History
            </span>
          </div>
        </header>

        {/* Content Section */}
        <section className="p-4 p-md-5">
          {/* Introduction */}
          <div
            className="alert alert-secondary border-0 rounded-3 shadow-sm"
            role="alert"
          >
            <div className="d-flex">
              <div className="me-3 fs-4">📱</div>
              <div>
                <p className="fs-5 lh-lg mb-0">
                  Before smartphones could stream 4K video, there was 3GP - the format that made video 
                  possible on early mobile phones. While largely obsolete today, understanding 3GP helps 
                  explain the evolution of mobile media and why certain compatibility issues still exist.
                </p>
              </div>
            </div>
          </div>

          {/* Important Note */}
          <div className="alert alert-info border-0 rounded-3 mb-5">
            <div className="d-flex">
              <div className="me-3 fs-5">💡</div>
              <div>
                <strong>Historical context:</strong> 3GP development began in 2000-2001 (3GPP Releases 99 and 4), 
                with formal specification TS 26.244 published in December 2003. It was designed when mobile data was expensive, 
                phone storage was measured in megabytes, and processors were much less powerful than today's smartphones.
              </div>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="card border-0 shadow-sm mb-5">
            <div className="card-header bg-secondary text-white py-3">
              <h2 className="h4 mb-0 fw-bold">What we're covering</h2>
            </div>
            <div className="card-body p-4">
              <div className="row">
                <div className="col-md-6">
                  <ul className="list-unstyled">
                    {[
                      "What 3GP actually is",
                      "Why it was revolutionary", 
                      "Technical specifications",
                      "File size advantages",
                      "Quality limitations",
                      "Modern alternatives",
                    ].map((item, index) => (
                      <li key={index} className="mb-2">
                        <a
                          href={`#section-${index + 1}`}
                          className="text-decoration-none text-secondary hover-text-dark"
                        >
                          {item}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul className="list-unstyled">
                    {[
                      "Conversion guide",
                      "Current relevance",
                      "When to use 3GP today",
                      
                    ].map((item, index) => (
                      <li key={index} className="mb-2">
                        <a
                          href={`#section-${index + 7}`}
                          className="text-decoration-none text-secondary hover-text-dark"
                        >
                          {item}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Section 1 */}
          <div id="section-1" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-secondary border-bottom pb-2">
              <span
                className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                1
              </span>
              What 3GP actually is (and isn't)
            </h2>
            <p className="text-secondary fs-5">
              3GP (3GPP file format) is a container format designed specifically for mobile devices. 
              It's not a video codec itself, but rather a wrapper that can contain various audio and video streams 
              optimized for the limitations of early mobile hardware.
            </p>
            <div className="row g-3 mt-3">
              {[
                {
                  icon: "📦",
                  text: "Container Format",
                  desc: "Wrapper for H.263, MPEG-4, AMR audio streams"
                },
                {
                  icon: "📱",
                  text: "Mobile-First",
                  desc: "Designed for 2000s-era mobile hardware"
                },
                {
                  icon: "🌐",
                  text: "3G Optimized",
                  desc: "Built for slow, expensive mobile data"
                },
                {
                  icon: "💾",
                  text: "Tiny Files",
                  desc: "Measured in MB when others used GB"
                },
                {
                  icon: "⚡",
                  text: "Low Complexity",
                  desc: "Simple decoding for weak mobile CPUs"
                },
                {
                  icon: "🔧",
                  text: "3GPP Standard",
                  desc: "Technical Specification TS 26.244"
                },
              ].map((item, index) => (
                <div key={index} className="col-6 col-md-4 col-lg-2">
                  <div className="p-3 border rounded-3 text-center bg-light hover-lift transition-all h-100">
                    <div className="fs-2 mb-2">{item.icon}</div>
                    <div className="small fw-medium text-dark">{item.text}</div>
                    <div className="text-muted small mt-1">{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 2 */}
          <div id="section-2" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-secondary border-bottom pb-2">
              <span
                className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                2
              </span>
              Why 3GP was revolutionary for its time
            </h2>
            <div className="row g-4">
              {[
                {
                  icon: "🎬",
                  title: "First Mobile Video",
                  desc: "Made video recording and playback possible on phones that previously could only handle images and audio"
                },
                {
                  icon: "📶",
                  title: "Network Optimized",
                  desc: "Designed to work reliably over slow, unstable 2G and 3G mobile connections"
                },
                {
                  icon: "🔋",
                  title: "Battery Efficient",
                  desc: "Low computational requirements meant less battery drain during playback"
                },
                {
                  icon: "💵",
                  title: "Cost Effective",
                  desc: "Small file sizes meant affordable storage and data usage"
                },
              ].map((item, index) => (
                <div key={index} className="col-md-6">
                  <div className="d-flex align-items-start p-3 border rounded-3 bg-light h-100">
                    <span className="fs-2 me-3">{item.icon}</span>
                    <div>
                      <h5 className="fw-bold mb-1">{item.title}</h5>
                      <p className="text-secondary mb-0">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="alert alert-info mt-4" role="alert">
              <div className="d-flex">
                <div className="me-3">📼</div>
                <div>
                  <strong>Real-world example:</strong> Early Nokia phones (like the Nokia 6630, N70) saved videos as 3GP files. 
                  A 30-second clip would be 2-3MB - significant when phones had only 8-16MB of internal storage. 
                  Users often had to delete old videos to make space for new ones.
                </div>
              </div>
            </div>
          </div>

          {/* Section 3 - Technical Specifications */}
          <div id="section-3" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-secondary border-bottom pb-2">
              <span
                className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                3
              </span>
              Technical specifications: What's inside a 3GP file
            </h2>
            <p className="text-muted mb-4">
              <em>Values shown are representative for many early mobile clips; actual values may vary based on device, codec and settings.</em>
            </p>
            <div className="table-responsive">
              <table className="table table-hover align-middle shadow-sm">
                <thead className="table-secondary">
                  <tr>
                    <th className="border-0">Component</th>
                    <th className="border-0">Typical Codec</th>
                    <th className="border-0">Specifications</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      component: "Video Stream",
                      codec: "H.263, MPEG-4 Part 2",
                      specs: "176x144 to 352x288 resolution, typically 15fps, ~128kbps bitrate"
                    },
                    {
                      component: "Audio Stream", 
                      codec: "AMR-NB, AMR-WB, AAC*",
                      specs: "4.75-12.2 kbps (AMR), 8kHz sample rate, mono"
                    },
                    {
                      component: "Container",
                      codec: "3GPP (.3gp, .3gpp)",
                      specs: "Based on MP4, optimized for mobile (3GPP TS 26.244)"
                    },
                    {
                      component: "File Size",
                      codec: "N/A",
                      specs: "Typically 1-5MB for 30-second clips"
                    },
                    {
                      component: "MIME Type",
                      codec: "N/A", 
                      specs: "video/3gpp, video/3gpp2"
                    },
                  ].map((row, index) => (
                    <tr key={index}>
                      <td className="fw-semibold">{row.component}</td>
                      <td><code>{row.codec}</code></td>
                      <td className="text-muted">{row.specs}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="alert alert-light mt-3" role="alert">
              <small>
                <strong>Note:</strong> Later 3GP variants could include H.264/AVC video and AAC audio codecs, 
                but early implementations primarily used H.263 and AMR-NB for maximum compatibility with early mobile hardware.
              </small>
            </div>
          </div>

          {/* Section 4 - File Size Advantages */}
          <div id="section-4" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-secondary border-bottom pb-2">
              <span
                className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                4
              </span>
              File size advantages: Why 3GP files are so small
            </h2>
            
            <div className="row g-4">
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-success text-white py-3">
                    <h5 className="mb-0 fw-bold">📏 Resolution</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>QCIF:</strong> 176x144 pixels</li>
                      <li><strong>CIF:</strong> 352x288 pixels</li>
                      <li><strong>Comparison:</strong> Approximately 10-12x smaller than 480p</li>
                      <li><strong>Modern equivalent:</strong> Thumbnail size</li>
                    </ul>
                    <div className="mt-3">
                      <span className="badge bg-success me-2">Tiny</span>
                      <span className="badge bg-success">Mobile Optimized</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-info text-white py-3">
                    <h5 className="mb-0 fw-bold">🎵 Audio Compression</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>AMR-NB:</strong> 4.75-12.2 kbps</li>
                      <li><strong>Mono only</strong> - no stereo</li>
                      <li><strong>8kHz sampling</strong> - telephone quality</li>
                      <li><strong>Comparison:</strong> 50x smaller than CD audio</li>
                    </ul>
                    <div className="mt-3">
                      <span className="badge bg-info me-2">Highly Compressed</span>
                      <span className="badge bg-info">Voice Optimized</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-warning text-dark py-3">
                    <h5 className="mb-0 fw-bold">⚡ Bitrate Limits</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>Video:</strong> Typically 64-128 kbps</li>
                      <li><strong>Audio:</strong> 5-12 kbps</li>
                      <li><strong>Total:</strong> ~150 kbps typical</li>
                      <li><strong>Comparison:</strong> Much smaller than HD video</li>
                    </ul>
                    <div className="mt-3">
                      <span className="badge bg-warning me-2">Low Bitrate</span>
                      <span className="badge bg-warning">3G Optimized</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="alert alert-info mt-4" role="alert">
              <strong>Real example:</strong> A 30-second 3GP video at CIF resolution (352x288) would be about 3-4MB. 
              The same duration at 720p would typically be 50-60MB - significantly larger!
            </div>
          </div>

          {/* Section 5 - Quality Limitations */}
          <div id="section-5" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-secondary border-bottom pb-2">
              <span
                className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                5
              </span>
              Quality limitations: The trade-offs for small file sizes
            </h2>
            <div className="row g-4">
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-danger text-white py-3">
                    <h5 className="mb-0 fw-bold">❌ Video Quality</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>Blocky artifacts</strong> from heavy compression</li>
                      <li><strong>Low resolution</strong> - max 352x288 pixels</li>
                      <li><strong>Limited frame rate</strong> - typically 15fps maximum</li>
                      <li><strong>Poor color reproduction</strong></li>
                      <li><strong>No HD support</strong></li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-warning text-dark py-3">
                    <h5 className="mb-0 fw-bold">🎵 Audio Quality</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>Telephone quality</strong> - 8kHz sampling</li>
                      <li><strong>Mono only</strong> - no stereo separation</li>
                      <li><strong>Heavy compression</strong> artifacts</li>
                      <li><strong>Limited frequency range</strong></li>
                      <li><strong>AMR codec limitations</strong></li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-lg-4">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-info text-white py-3">
                    <h5 className="mb-0 fw-bold">📉 Why It Declined</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>HD demand</strong> - consumers wanted better quality</li>
                      <li><strong>Better codecs</strong> - H.264 offered superior compression</li>
                      <li><strong>Storage growth</strong> - phones gained GBs of storage</li>
                      <li><strong>Faster networks</strong> - 4G/LTE made larger files practical</li>
                      <li><strong>Streaming services</strong> - required modern formats</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 6 - Modern Alternatives */}
          <div id="section-6" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-secondary border-bottom pb-2">
              <span
                className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                6
              </span>
              Modern alternatives: What replaced 3GP
            </h2>
            <p className="text-muted mb-4">
              <em>Quality improvement is qualitative, not a strict numeric multiplier.</em>
            </p>
            
            <div className="table-responsive">
              <table className="table table-hover align-middle shadow-sm">
                <thead className="table-info">
                  <tr>
                    <th className="border-0">Format</th>
                    <th className="border-0">Quality Improvement</th>
                    <th className="border-0">File Size Comparison</th>
                    <th className="border-0">Mobile Support</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      format: "3GP",
                      quality: "Baseline (poor)",
                      size: "Baseline",
                      support: "Legacy phones only"
                    },
                    {
                      format: "MP4/H.264",
                      quality: "Significantly better",
                      size: "Similar or smaller",
                      support: "Universal (2003+)"
                    },
                    {
                      format: "WebM/VP9",
                      quality: "Much better", 
                      size: "Typically smaller",
                      support: "Modern browsers"
                    },
                    {
                      format: "MP4/HEVC",
                      quality: "Dramatically better",
                      size: "Typically smaller",
                      support: "2013+ devices"
                    },
                    {
                      format: "AV1",
                      quality: "Excellent",
                      size: "Typically smaller than HEVC",
                      support: "2018+ devices"
                    },
                  ].map((row, index) => (
                    <tr key={index}>
                      <td className="fw-semibold">{row.format}</td>
                      <td className={row.quality !== 'Baseline (poor)' ? 'text-success' : ''}>{row.quality}</td>
                      <td className={row.size.includes('smaller') ? 'text-success' : ''}>{row.size}</td>
                      <td>
                        <span className="badge bg-info">{row.support}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="alert alert-success mt-4" role="alert">
              <strong>Key insight:</strong> Modern codecs like H.264 can achieve significantly better quality at similar file sizes as 3GP, 
              or similar quality at much smaller file sizes. The technological improvements have been substantial.
            </div>
          </div>

          {/* Section 7 - Conversion Guide */}
          <div id="section-7" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-secondary border-bottom pb-2">
              <span
                className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                7
              </span>
              Conversion guide: Moving from 3GP to modern formats
            </h2>
            <div className="row g-3">
              {[
                "Use FFmpeg: ffmpeg -i input.3gp output.mp4 (lossy re-encoding)",
                "Target H.264 video codec for maximum compatibility",
                "Use AAC audio at 128kbps for good quality",
                "Set resolution to 480p or higher for modern displays", 
                "Maintain original frame rate or increase to 24-30fps",
                "Keep original aspect ratio to avoid distortion",
              ].map((tip, index) => (
                <div key={index} className="col-md-6">
                  <div className="d-flex align-items-center p-3 border rounded-3 bg-light h-100">
                    <div
                      className="bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                      style={{ width: "30px", height: "30px" }}
                    >
                      {index + 1}
                    </div>
                    <span className="fw-medium">{tip}</span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 p-4 bg-light rounded-3">
              <h5 className="fw-bold mb-3">Recommended Conversion Settings:</h5>
              <div className="row">
                <div className="col-md-6">
                  <ul className="text-secondary">
                    <li><strong>Video Codec:</strong> H.264 (libx264)</li>
                    <li><strong>Resolution:</strong> 640x480 or 1280x720</li>
                    <li><strong>Bitrate:</strong> 1-2 Mbps for SD, 3-5 Mbps for HD</li>
                    <li><strong>Frame Rate:</strong> 24-30 fps</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <ul className="text-secondary">
                    <li><strong>Audio Codec:</strong> AAC</li>
                    <li><strong>Audio Bitrate:</strong> 128 kbps</li>
                    <li><strong>Channels:</strong> Stereo</li>
                    <li><strong>Container:</strong> MP4</li>
                  </ul>
                </div>
              </div>
              <div className="mt-3 p-3 bg-warning rounded">
                <small className="text-dark">
                  <strong>Important:</strong> Converting 3GP to modern formats won't improve the original quality 
                  (you can't add detail that wasn't recorded), but it will make files more compatible with modern devices and easier to share.
                </small>
              </div>
            </div>
          </div>

          {/* Section 8 - Current Relevance */}
          <div id="section-8" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-secondary border-bottom pb-2">
              <span
                className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                8
              </span>
              Current relevance: Where 3GP still matters today
            </h2>
            <div className="row g-4">
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-warning text-dark py-3">
                    <h5 className="mb-0">✅ Still Relevant For</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>Legacy device support</strong> - older feature phones</li>
                      <li><strong>MMS messaging</strong> - some legacy systems may still use 3GP</li>
                      <li><strong>Archival purposes</strong> - preserving old mobile videos</li>
                      <li><strong>Embedded systems</strong> - some IoT devices with limited storage</li>
                      <li><strong>Backward compatibility</strong> testing</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-header bg-danger text-white py-3">
                    <h5 className="mb-0">❌ Obsolete For</h5>
                  </div>
                  <div className="card-body">
                    <ul className="text-secondary">
                      <li><strong>Modern smartphones</strong> - all support better formats</li>
                      <li><strong>Web video</strong> - use MP4/WebM instead</li>
                      <li><strong>High-quality recording</strong> - inadequate resolution</li>
                      <li><strong>Streaming services</strong> - insufficient quality</li>
                      <li><strong>Professional use</strong> - quality too poor</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            <div className="alert alert-info mt-4" role="alert">
              <strong>MMS note:</strong> While largely obsolete, 3GP may still survive in very legacy MMS/feature phone environments. 
              Most modern carriers have upgraded to supporting MP4/H.264 in MMS, but some older systems may still convert to 3GP for compatibility.
            </div>
          </div>

          {/* Section 9 - When to Use 3GP Today */}
          <div id="section-9" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-secondary border-bottom pb-2">
              <span
                className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                9
              </span>
              When to use 3GP in 2024: The practical guide
            </h2>
            
            <div className="row g-4 text-center">
              <div className="col-md-4">
                <div className="p-4 border rounded-3 bg-light h-100">
                  <div className="fs-1 mb-3">👍</div>
                  <h5 className="fw-bold text-success">Use 3GP For</h5>
                  <ul className="text-secondary text-start">
                    <li>Legacy device compatibility</li>
                    <li>MMS video messaging</li>
                    <li>Extremely limited storage</li>
                    <li>Very slow network conditions</li>
                    <li>Archival of old 3GP files</li>
                  </ul>
                </div>
              </div>
              <div className="col-md-4">
                <div className="p-4 border rounded-3 bg-light h-100">
                  <div className="fs-1 mb-3">🤔</div>
                  <h5 className="fw-bold text-warning">Consider Alternatives</h5>
                  <ul className="text-secondary text-start">
                    <li>New video recordings</li>
                    <li>Web content</li>
                    <li>Mobile apps</li>
                    <li>Social media</li>
                    <li>Professional work</li>
                  </ul>
                </div>
              </div>
              <div className="col-md-4">
                <div className="p-4 border rounded-3 bg-light h-100">
                  <div className="fs-1 mb-3">👎</div>
                  <h5 className="fw-bold text-danger">Never Use 3GP For</h5>
                  <ul className="text-secondary text-start">
                    <li>HD video content</li>
                    <li>Modern smartphones</li>
                    <li>Streaming platforms</li>
                    <li>Video editing masters</li>
                    <li>High-quality audio</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Quick Decision Framework */}
            <div className="mt-5 p-4 bg-light rounded-3">
              <h5 className="fw-bold mb-3">Quick Decision Framework:</h5>
              <div className="row text-center">
                <div className="col-md-3 mb-3">
                  <div className="p-3 border rounded bg-white">
                    <div className="fs-4 mb-2">📱</div>
                    <h6 className="fw-bold">Device Age</h6>
                    <p className="small mb-1"><strong>Pre-2010:</strong> 3GP okay</p>
                    <p className="small mb-0"><strong>Post-2010:</strong> Use MP4</p>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="p-3 border rounded bg-white">
                    <div className="fs-4 mb-2">💾</div>
                    <h6 className="fw-bold">Storage</h6>
                    <p className="small mb-1"><strong>Under 100MB:</strong> 3GP okay</p>
                    <p className="small mb-0"><strong>Over 100MB:</strong> Use MP4</p>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="p-3 border rounded bg-white">
                    <div className="fs-4 mb-2">🎯</div>
                    <h6 className="fw-bold">Quality Need</h6>
                    <p className="small mb-1"><strong>Basic:</strong> 3GP okay</p>
                    <p className="small mb-0"><strong>Good+:</strong> Use MP4</p>
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <div className="p-3 border rounded bg-white">
                    <div className="fs-4 mb-2">🔗</div>
                    <h6 className="fw-bold">Network</h6>
                    <p className="small mb-1"><strong>2G/3G:</strong> 3GP okay</p>
                    <p className="small mb-0"><strong>4G+:</strong> Use MP4</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 10 - FAQ */}
          {/* <div id="section-10" className="mb-5">
            <h2 className="h3 fw-bold mb-4 text-secondary border-bottom pb-2">
              <span
                className="bg-secondary text-white rounded-circle d-inline-flex align-items-center justify-content-center me-2"
                style={{ width: "40px", height: "40px" }}
              >
                10
              </span>
              Your 3GP Questions Answered
            </h2>
            
            <div className="accordion" id="threegpFAQ">
              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq1">
                    Is 3GP still used anywhere today?
                  </button>
                </h3>
                <div id="faq1" className="accordion-collapse collapse" data-bs-parent="#threegpFAQ">
                  <div className="accordion-body">
                    <p><strong>Yes, but in very limited contexts:</strong></p>
                    <ul>
                      <li><strong>MMS messaging:</strong> In rare legacy mobile carrier systems that may still convert videos to 3GP for MMS delivery</li>
                      <li><strong>Legacy devices:</strong> Older feature phones and embedded systems</li>
                      <li><strong>Backward compatibility:</strong> Some systems maintain 3GP support for old content</li>
                      <li><strong>Specific regions:</strong> Areas with older mobile infrastructure</li>
                    </ul>
                    <p>For any new projects or modern devices, MP4 with H.264 is the recommended replacement.</p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq2">
                    Can modern smartphones still play 3GP files?
                  </button>
                </h3>
                <div id="faq2" className="accordion-collapse collapse" data-bs-parent="#threegpFAQ">
                  <div className="accordion-body">
                    <p><strong>Most modern smartphones have limited 3GP support:</strong></p>
                    <ul>
                      <li><strong>iOS:</strong> Historical QuickTime framework support; modern devices have limited compatibility</li>
                      <li><strong>Android:</strong> Supported via built-in codecs but experience varies by device</li>
                      <li><strong>Windows Phone:</strong> Generally supported on older versions</li>
                    </ul>
                    <p><strong>However, the experience is poor:</strong></p>
                    <ul>
                      <li>Low resolution looks terrible on HD screens</li>
                      <li>Audio quality is noticeably poor</li>
                      <li>Some apps may not recognize the format</li>
                      <li>No hardware acceleration for old codecs</li>
                    </ul>
                    <p>While technically possible on some devices, it's not recommended for new content.</p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq3">
                    What's the difference between 3GP and MP4?
                  </button>
                </h3>
                <div id="faq3" className="accordion-collapse collapse" data-bs-parent="#threegpFAQ">
                  <div className="accordion-body">
                    <p><strong>3GP is actually based on MP4, but with specific limitations:</strong></p>
                    <div className="row mt-3">
                      <div className="col-md-6">
                        <strong>3GP (Specialized):</strong>
                        <ul>
                          <li>Mobile-optimized subset of MP4</li>
                          <li>Limited to specific codecs (H.263, AMR)</li>
                          <li>Maximum 352x288 resolution</li>
                          <li>Designed for 2000s mobile hardware</li>
                          <li>Very small file sizes</li>
                          <li>File extensions: .3gp, .3gpp</li>
                        </ul>
                      </div>
                      <div className="col-md-6">
                        <strong>MP4 (General Purpose):</strong>
                        <ul>
                          <li>Full-featured container format</li>
                          <li>Supports modern codecs (H.264, H.265, AAC)</li>
                          <li>Supports up to 8K resolution</li>
                          <li>Works on all modern devices</li>
                          <li>Flexible file sizes</li>
                          <li>File extension: .mp4</li>
                        </ul>
                      </div>
                    </div>
                    <p className="mt-3"><strong>Think of it this way:</strong> 3GP is to MP4 what a flip phone is to a smartphone - 
                    both can make calls, but one is severely limited compared to the other.</p>
                  </div>
                </div>
              </div>

              <div className="accordion-item border-0 mb-3">
                <h3 className="accordion-header">
                  <button className="accordion-button collapsed bg-light text-dark fw-bold py-3" type="button" data-bs-toggle="collapse" data-bs-target="#faq4">
                    Should I convert my old 3GP videos to modern formats?
                  </button>
                </h3>
                <div id="faq4" className="accordion-collapse collapse" data-bs-parent="#threegpFAQ">
                  <div className="accordion-body">
                    <p><strong>Yes, for several important reasons:</strong></p>
                    <ul>
                      <li><strong>Future compatibility:</strong> 3GP support may disappear from future devices</li>
                      <li><strong>Better playback:</strong> Modern formats work better on current hardware</li>
                      <li><strong>Editing flexibility:</strong> Modern formats are easier to edit and process</li>
                      <li><strong>Sharing:</strong> Social media and messaging apps prefer MP4</li>
                      <li><strong>Archival:</strong> MP4 is a more future-proof format</li>
                    </ul>
                    <p><strong>Conversion won't improve quality</strong> (you can't add detail that wasn't recorded), 
                    but it will make the files more usable and accessible on modern systems.</p>
                    <p><strong>Recommended target:</strong> MP4 with H.264 video and AAC audio at 720p resolution (even if upscaled).</p>
                  </div>
                </div>
              </div>
            </div>
          </div> */}

          {/* Final CTA */}
          <div
            className="text-center mt-5 p-5 rounded-4"
            style={{
              background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
            }}
          >
            <h2 className="h1 fw-bold mb-3 text-secondary">
              Working with legacy 3GP files?
            </h2>
            <p className="fs-5 text-secondary mb-4">
              Get our curated collection of 3GP sample files for testing and conversion.
            </p>
            <div className="d-flex flex-column flex-sm-row justify-content-center gap-3">
              <a
                href="https://samplefiles.dev/sample-videos/sample-3gp"
                className="btn btn-secondary btn-lg px-5 py-3 shadow hover-lift"
                target="_blank"
                rel="noopener noreferrer"
              >
                📱 Get 3GP Sample Files
              </a>
              <a
                href="#section-1"
                className="btn btn-outline-secondary btn-lg px-5 py-3"
              >
                📖 Read This Again
              </a>
            </div>
            <p className="mt-3 text-muted small">
              Various durations and qualities - perfect for testing legacy compatibility.
            </p>
          </div>
        </section>
      </article>
    </div>
  );
};

export default ThreeGPFormatGuide;

// "Common questions answered",